import { useState } from 'react';
import Button from '../../../components/ui/Button';
import LoanRepaymentForm from './LoanRepaymentForm';
import type { ActiveLoanRow } from '../../../services/loanService';
import type { LoanFormData } from '../types';

interface LoanActiveCardProps {
  loan: ActiveLoanRow;
  currency: string;
  canRecordPayment: boolean;
  onRecordPayment: (loanId: string, data: LoanFormData) => Promise<void>;
}

const LoanActiveCard = ({
  loan,
  currency,
  canRecordPayment,
  onRecordPayment,
}: LoanActiveCardProps) => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const memberName = loan.member?.full_name || loan.member?.email || 'Unknown member';
  const totalAmount = Number(loan.total_amount || 0);
  const remaining = Number(loan.remaining_balance || 0);
  const paid = Math.max(0, totalAmount - remaining);
  const progress = totalAmount > 0 ? Math.min(100, (paid / totalAmount) * 100) : 0;

  const handleSubmit = async (data: LoanFormData) => {
    setIsSubmitting(true);
    try {
      await onRecordPayment(loan.id, data);
      setShowForm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-md p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{memberName}</h3>
          <p className="text-xs text-muted-foreground capitalize">
            {loan.loan_request?.purpose || 'Loan'}
          </p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          loan.status === 'completed'
            ? 'bg-muted text-muted-foreground'
            : 'bg-success/10 text-success'
        }`}>
          {loan.status}
        </span>
      </div>

      <div className="mb-3">
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span>Progress</span>
          <span>{progress.toFixed(0)}%</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground mb-4">
        <div>
          <span className="block text-foreground font-medium">
            {Number(loan.principal_amount).toLocaleString()} {currency}
          </span>
          Principal
        </div>
        <div>
          <span className="block text-foreground font-medium">
            {remaining.toLocaleString()} {currency}
          </span>
          Remaining
        </div>
        <div>
          <span className="block text-foreground font-medium">
            {Number(loan.monthly_payment).toLocaleString()} {currency}
          </span>
          Monthly
        </div>
        <div>
          <span className="block text-foreground font-medium">
            {loan.months_paid || 0} / {loan.repayment_months}
          </span>
          Payments made
        </div>
      </div>

      {canRecordPayment && loan.status === 'active' && remaining > 0.01 && (
        <>
          {!showForm ? (
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowForm(true)}
              iconName="Plus"
              fullWidth
            >
              Record Payment
            </Button>
          ) : (
            <div className="border-t border-border pt-4 mt-2">
              <LoanRepaymentForm
                loanId={loan.id}
                loanAmount={Number(loan.principal_amount)}
                remainingBalance={remaining}
                defaultAmount={Math.min(Number(loan.monthly_payment), remaining)}
                onSubmit={handleSubmit}
                onCancel={() => setShowForm(false)}
                isSubmitting={isSubmitting}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LoanActiveCard;
